import {
  BaseStore,
  type Operation,
  type OperationResults,
  type ListNamespacesOperation,
  type PutOperation,
  type GetOperation,
  type Item,
  type MatchCondition,
} from "@langchain/langgraph";

import { MemoryClient } from "@cloudbase/agent-agents";

/**
 * TDAI Store configuration
 */
export interface TDAIStoreConfig {
  /**
   * TDAI Memory Client instance for long-term storage
   */
  memoryClient: MemoryClient;

  /**
   * Session ID for storing records
   */
  sessionId: string;

  /**
   * Optional namespace prefix for all operations
   */
  namespacePrefix?: string[];

  /**
   * TTL configuration for records
   */
  ttl?: {
    defaultTtlSeconds?: number;
    sweepIntervalMinutes?: number;
  };

  /**
   * Whether to ensure tables/collections exist on startup
   */
  ensureTables?: boolean;

  /**
   * Default strategy for storing records
   */
  defaultStrategy?: string;
}

/**
 * Filter operators for advanced filtering
 */
export interface FilterOperators {
  $eq?: unknown;
  $ne?: unknown;
  $gt?: number | Date;
  $gte?: number | Date;
  $lt?: number | Date;
  $lte?: number | Date;
  $in?: unknown[];
  $nin?: unknown[];
  $exists?: boolean;
  $regex?: string;
}

/**
 * TDAI implementation of the BaseStore interface.
 * Uses TDAI Memory Client for long-term record storage.
 */
export class TDAIStore extends BaseStore {
  private client: MemoryClient;
  private namespacePrefix: string[];
  private ttlConfig?: TDAIStoreConfig["ttl"];
  private ensureTables: boolean;
  private isSetup: boolean = false;
  private isClosed: boolean = false;
  private sweepInterval?: NodeJS.Timeout;
  private sessionId: string;
  private defaultStrategy: string;

  constructor(config: TDAIStoreConfig) {
    super();

    this.client = config.memoryClient;
    this.namespacePrefix = config.namespacePrefix || [];
    this.ttlConfig = config.ttl;
    this.ensureTables = config.ensureTables ?? true;
    this.sessionId = config.sessionId || "default_session";
    this.defaultStrategy = config.defaultStrategy || "store";
  }

  /**
   * Create a storage key from namespace and key
   */
  private createStorageKey(namespace: string[], key: string): string {
    const fullNamespace = [...this.namespacePrefix, ...namespace];
    return `${fullNamespace.join(":")}:${key}`;
  }

  /**
   * Parse a storage key back to namespace and key
   */
  private parseStorageKey(storageKey: string): {
    namespace: string[];
    key: string;
  } {
    const parts = storageKey.split(":");
    const prefixLength = this.namespacePrefix.length;

    // Remove the prefix from the namespace
    const namespace = parts.slice(prefixLength, -1);
    const key = parts[parts.length - 1];

    return { namespace, key };
  }

  /**
   * Put an item with optional TTL.
   */
  async put(
    namespace: string[],
    key: string,
    value: Record<string, unknown>,
    index?: false | string[],
    options?: { ttl?: number }
  ): Promise<void> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    const storageKey = this.createStorageKey(namespace, key);

    // Prepare the content with metadata
    const content = JSON.stringify({
      storageKey,
      namespace,
      key,
      value,
      index,
      ttl: options?.ttl || this.ttlConfig?.defaultTtlSeconds,
    });

    await this.client.appendRecord({
      sessionId: this.sessionId,
      content,
      strategy: this.defaultStrategy,
    });
  }

  /**
   * Get an item by namespace and key.
   */
  async get(namespace: string[], key: string): Promise<Item | null> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    const storageKey = this.createStorageKey(namespace, key);

    try {
      const { records = [] } = await this.client.searchRecords({
        content: storageKey,
        sessionId: this.sessionId,
        limit: 1,
      });

      if (!records.length) {
        return null;
      }

      const record = records[0];
      const data = JSON.parse(record.record_content);

      // Check TTL if present
      if (data.ttl && record.created_at) {
        const createdTime = new Date(record.created_at).getTime();
        const now = Date.now();
        if (now > createdTime + data.ttl * 1000) {
          // Item has expired, delete it
          await this.delete(namespace, key);
          return null;
        }
      }

      return {
        namespace,
        key,
        value: data.value,
        createdAt: new Date(record.created_at || Date.now()),
        updatedAt: new Date(record.updated_at || Date.now()),
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Delete an item by namespace and key.
   */
  async delete(namespace: string[], key: string): Promise<void> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    const storageKey = this.createStorageKey(namespace, key);

    try {
      const { records = [] } = await this.client.searchRecords({
        content: storageKey,
        sessionId: this.sessionId,
        limit: 1,
      });

      const record = records[0];
      if (record) {
        await this.client.deleteRecord({
          sessionId: this.sessionId,
          recordId: record.record_id,
        });
      }
    } catch (error) {
      // Ignore errors if item doesn't exist
    }
  }

  /**
   * List namespaces with optional filtering.
   */
  async listNamespaces(
    options: {
      prefix?: string[];
      suffix?: string[];
      maxDepth?: number;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<string[][]> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    const { prefix, suffix, maxDepth, limit = 100, offset = 0 } = options;

    try {
      // Get all records to extract namespaces
      const { records = [] } = await this.client.queryRecords({
        limit: 1000, // Large limit to get all records
      });

      const namespaceSet = new Set<string>();

      for (const record of records) {
        try {
          const data = JSON.parse(record.record_content);
          if (data.namespace) {
            const namespace = data.namespace;

            // Apply prefix filter if specified
            if (prefix && prefix.length > 0) {
              const hasPrefix = prefix.every((p, i) => namespace[i] === p);
              if (!hasPrefix) continue;
            }

            // Apply suffix filter if specified
            if (suffix && suffix.length > 0) {
              const namespaceSuffix = namespace.slice(-suffix.length);
              if (JSON.stringify(namespaceSuffix) !== JSON.stringify(suffix)) {
                continue;
              }
            }

            // Apply maxDepth filter if specified
            if (maxDepth !== undefined && namespace.length > maxDepth) {
              continue;
            }

            namespaceSet.add(JSON.stringify(namespace));
          }
        } catch (error) {
          // Skip invalid records
        }
      }

      // Convert back to arrays and apply pagination
      const namespaces = Array.from(namespaceSet)
        .map((ns) => JSON.parse(ns))
        .sort()
        .slice(offset, offset + limit);

      return namespaces;
    } catch (error) {
      return [];
    }
  }

  /**
   * Execute multiple operations in a single batch.
   */
  async batch<Op extends Operation[]>(
    operations: Op
  ): Promise<OperationResults<Op>> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    const results: unknown[] = [];

    for (const operation of operations) {
      if ("namespacePrefix" in operation) {
        // SearchOperation
        results.push(await this.executeSearch(operation));
      } else if ("key" in operation && !("value" in operation)) {
        // GetOperation
        const getOp = operation as GetOperation;
        results.push(await this.get(getOp.namespace, getOp.key));
      } else if ("value" in operation) {
        // PutOperation
        const putOp = operation as PutOperation & {
          options?: { ttl?: number };
        };
        if (putOp.value !== null) {
          await this.put(
            putOp.namespace,
            putOp.key,
            putOp.value,
            putOp.index,
            putOp.options
          );
        }
        results.push(undefined);
      } else if ("matchConditions" in operation) {
        // ListNamespacesOperation
        const listOp = operation as ListNamespacesOperation;
        results.push(await this.executeListNamespaces(listOp));
      } else {
        throw new Error(
          `Unsupported operation type: ${JSON.stringify(operation)}`
        );
      }
    }

    return results as OperationResults<Op>;
  }

  /**
   * Execute search operation
   */
  private async executeSearch(operation: any): Promise<Item[]> {
    const { namespacePrefix, ...searchOptions } = operation;
    return this.search(namespacePrefix, searchOptions);
  }

  /**
   * Execute list namespaces operation
   */
  private async executeListNamespaces(
    operation: ListNamespacesOperation
  ): Promise<string[][]> {
    const { matchConditions, maxDepth, limit = 100, offset = 0 } = operation;

    // Convert match conditions to prefix/suffix filters
    let prefix: string[] | undefined;
    let suffix: string[] | undefined;

    if (matchConditions && matchConditions.length > 0) {
      for (const condition of matchConditions) {
        if (condition.matchType === "prefix") {
          prefix = condition.path;
        } else if (condition.matchType === "suffix") {
          suffix = condition.path;
        }
      }
    }

    return this.listNamespaces({
      prefix,
      suffix,
      maxDepth,
      limit,
      offset,
    });
  }

  /**
   * Initialize the store.
   */
  async setup(): Promise<void> {
    if (this.isSetup) return;

    // Start TTL sweeper if configured
    if (this.ttlConfig?.sweepIntervalMinutes) {
      const intervalMs = this.ttlConfig.sweepIntervalMinutes * 60 * 1000;
      this.sweepInterval = setInterval(async () => {
        try {
          await this.sweepExpiredItems();
        } catch (error) {
          console.error("Error during TTL sweep:", error);
        }
      }, intervalMs);
    }

    this.isSetup = true;
  }

  /**
   * Start the store.
   */
  async start(): Promise<void> {
    if (this.ensureTables && !this.isSetup) {
      await this.setup();
    }
  }

  /**
   * Stop the store and close all connections.
   */
  async stop(): Promise<void> {
    if (this.isClosed) return;

    if (this.sweepInterval) {
      clearInterval(this.sweepInterval);
      this.sweepInterval = undefined;
    }

    this.client.close();
    this.isClosed = true;
  }

  /**
   * Manually sweep expired items from the store.
   */
  async sweepExpiredItems(): Promise<number> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    try {
      const { records = [] } = await this.client.queryRecords({
        limit: 1000,
      });

      let cleanedCount = 0;
      const now = Date.now();

      for (const record of records) {
        try {
          const data = JSON.parse(record.record_content);
          if (data.ttl && data.createdAt) {
            const createdTime = new Date(data.createdAt).getTime();
            if (now > createdTime + data.ttl * 1000) {
              await this.client.deleteRecord({
                sessionId: this.sessionId,
                recordId: record.record_id,
              });
              cleanedCount++;
            }
          }
        } catch (error) {
          // Skip invalid records
        }
      }

      return cleanedCount;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Get statistics about the store.
   */
  async getStats(): Promise<{
    totalItems: number;
    expiredItems: number;
    namespaceCount: number;
    oldestItem: Date | null;
    newestItem: Date | null;
  }> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    try {
      const { records = [] } = await this.client.queryRecords({
        limit: 1000,
      });

      let totalItems = 0;
      let expiredItems = 0;
      const namespaces = new Set<string>();
      const dates: Date[] = [];
      const now = Date.now();

      for (const record of records) {
        try {
          const data = JSON.parse(record.record_content);
          totalItems++;

          if (data.namespace) {
            namespaces.add(data.namespace.join(":"));
          }

          if (data.createdAt) {
            dates.push(new Date(data.createdAt));
          }

          if (data.ttl && data.createdAt) {
            const createdTime = new Date(data.createdAt).getTime();
            if (now > createdTime + data.ttl * 1000) {
              expiredItems++;
            }
          }
        } catch (error) {
          // Skip invalid records
        }
      }

      const oldestItem =
        dates.length > 0
          ? new Date(Math.min(...dates.map((d) => d.getTime())))
          : null;
      const newestItem =
        dates.length > 0
          ? new Date(Math.max(...dates.map((d) => d.getTime())))
          : null;

      return {
        totalItems,
        expiredItems,
        namespaceCount: namespaces.size,
        oldestItem,
        newestItem,
      };
    } catch (error) {
      return {
        totalItems: 0,
        expiredItems: 0,
        namespaceCount: 0,
        oldestItem: null,
        newestItem: null,
      };
    }
  }

  /**
   * Search for items in the store with support for text search and filtering.
   */
  async search(
    namespacePrefix: string[],
    options: {
      /**
       * Filter conditions with support for advanced operators.
       */
      filter?: Record<
        string,
        string | number | boolean | null | FilterOperators
      >;

      /**
       * Natural language search query.
       */
      query?: string;

      /**
       * Maximum number of results to return.
       * @default 10
       */
      limit?: number;

      /**
       * Number of results to skip for pagination.
       * @default 0
       */
      offset?: number;

      /**
       * Whether to refresh TTL for returned items.
       */
      refreshTtl?: boolean;
    } = {}
  ): Promise<Item[]> {
    if (!this.isSetup && this.ensureTables) {
      await this.setup();
    }

    const { filter, query, limit = 10, offset = 0, refreshTtl } = options;

    try {
      // Build search query
      const namespaceKey = [...this.namespacePrefix, ...namespacePrefix].join(
        ":"
      );

      let searchResult;
      if (query) {
        searchResult = await this.client.searchRecords({
          content: query,
          limit: limit + offset,
        });
      } else {
        searchResult = await this.client.queryRecords({
          limit: limit + offset,
        });
      }

      const items: Item[] = [];
      const now = Date.now();

      if (searchResult.records) {
        for (const record of searchResult.records.slice(
          offset,
          offset + limit
        )) {
          try {
            const data = JSON.parse(record.record_content);

            // Filter by namespace prefix
            if (namespaceKey && !data.storageKey?.startsWith(namespaceKey)) {
              continue;
            }

            // Check TTL
            if (data.ttl && data.createdAt) {
              const createdTime = new Date(data.createdAt).getTime();
              if (now > createdTime + data.ttl * 1000) {
                continue; // Skip expired items
              }
            }

            // Apply filters (basic implementation)
            if (filter) {
              let matches = true;
              for (const [key, value] of Object.entries(filter)) {
                if (data.value[key] !== value) {
                  matches = false;
                  break;
                }
              }
              if (!matches) continue;
            }

            const item: Item = {
              namespace: data.namespace,
              key: data.key,
              value: data.value,
              createdAt: new Date(record.created_at || Date.now()),
              updatedAt: new Date(record.updated_at || Date.now()),
            };

            items.push(item);

            // Refresh TTL if requested
            if (refreshTtl && this.ttlConfig?.defaultTtlSeconds) {
              await this.put(data.namespace, data.key, data.value, undefined, {
                ttl: this.ttlConfig.defaultTtlSeconds,
              });
            }
          } catch (error) {
            // Skip invalid records
          }
        }
      }

      return items;
    } catch (error) {
      return [];
    }
  }
}

